"use client";

import ProductGrid from "@/components/gridPage/ProductGrid";
import Sidebar from "@/components/gridPage/sidebar/SideBar";
import { Category } from "@/packages/types/category";
import { useSearchParams } from "next/navigation";
import { use, useEffect, useMemo, useState, Suspense } from "react";
import { fetchGridProducts } from "@/api-service/product";
import { Product } from "@/packages/types/products";
import SelectField from "@/components/common/SelectBox";
import { fetchBrandBySlug } from "@/api-service/brands";
import { BrandRelatedRespone } from "@/packages/types/brands";
import { Button } from "@/components/ui/button";
import { useFastRouter } from "@/packages/hooks/useFastRouter";

import FaqAccordion from "@/components/common/FaqAccordion";
import ProductNotFound from "@/components/common/ProductNotFound";


interface BrandPageProps {
  params: Promise<{ slug: string }>;
}

function decodeHtml(html: string) {
  if (!html) return "";
  return html
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, "&");
}

function BannerImage({ src, alt }: { src?: string | null; alt: string }) {
  const [hasError, setHasError] = useState(false);
  useEffect(() => { setHasError(false); }, [src]);

  if (!src || hasError) return null;

  return (
    <div className="w-full aspect-[4/1] relative overflow-hidden rounded-2xl shadow-xs border border-slate-100 mb-6">
      <img
        src={src}
        alt={alt}
        className="w-full h-full object-cover"
        onError={() => setHasError(true)}
      />
    </div>
  );
}


function BrandPageContent({ params }: BrandPageProps) {


  const { slug } = use(params);
  const searchParams = useSearchParams();
  const { fastPush } = useFastRouter();

  /* ---------------- URL Derived Values ---------------- */

  const category = useMemo(() => {
    const cats = searchParams.get("categories");
    return cats ? cats.split(",").map(Number) : [];
  }, [searchParams]);

  const startPriceParam = searchParams.get("startPrice");
  const endPriceParam = searchParams.get("endPrice");
  const startPrice = startPriceParam ? Number(startPriceParam) : undefined;
  const endPrice = endPriceParam ? Number(endPriceParam) : undefined;

  const sortFromUrl = searchParams.get("sort") || "";

  /* ---------------- State ---------------- */
  const [categoryData, setCategoryData] = useState<Category>();
  const [products, setProducts] = useState<Product[]>();
  const [limit, setLimit] = useState(20);
  const [totalPages, setTotalPages] = useState(1);


  const [sort, setSort] = useState<string>(sortFromUrl);
  const [brandName, setBrandName] = useState<string>("");
  const [brandNotFound, setBrandNotFound] = useState(false);

  function toRootCategory(response: BrandRelatedRespone): Category {
    return {
      data: {
        id: response.data.id,
        name: response.data.name || "Brand",
        slug: response.data.slug || "brand",
        bannerUrl: response.data.bannerUrl || null,
        imageUrl: response.data.logoUrl || null,
        description: response.data.description || null,
        faqs: response.data.faqs || [],
        parentId: null,
        galleryImages: [],
        galleryImageUrls: [],
        children: [],
        minPrice: "",
        maxPrice: "",
      },
    };
  }



  /* ---------------- Fetch Brand ---------------- */
  useEffect(() => {
    const fetchData = async () => {
      try {
        setBrandNotFound(false);
        const res = await fetchBrandBySlug(slug);
        if (!res || !res.data || !res.data.id) {
          setBrandNotFound(true);
        } else {
          const formatted = toRootCategory(res);
          setCategoryData(formatted);
          if (res?.data?.name) {
            setBrandName(res.data.name);
          }
        }
      } catch (error: unknown) {
        console.error("Error fetching brand:", error);
        setBrandNotFound(true);
      }
    };

    fetchData();
  }, [slug]);

  /* ---------------- Responsive Limit ---------------- */
  useEffect(() => {
    const updateLimit = () => {
      setLimit(window.innerWidth <= 768 ? 16 : 20);
    };

    updateLimit();
    window.addEventListener("resize", updateLimit);
    return () => window.removeEventListener("resize", updateLimit);
  }, []);

  const [currentPage, setCurrentPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);

  /* ---------------- Reset page on filter changes ---------------- */
  useEffect(() => {
    setCurrentPage(1);
  }, [category, startPrice, endPrice, sort, slug, categoryData]);

  /* ---------------- Fetch Products ---------------- */
  useEffect(() => {
    const fetchData = async () => {
      try {
        if (currentPage > 1) setLoadingMore(true);
        const res = await fetchGridProducts(
          category,
          currentPage,
          limit,
          startPrice,
          endPrice,
          sort,
          undefined,
          categoryData?.data.id
        );

        if (currentPage === 1) {
          setProducts(res.data.products);
        } else {
          setProducts((prev) => [...(prev || []), ...(res.data.products || [])]);
        }
        setTotalPages(res.data.totalPages ?? 1);
      } catch (error: unknown) {
        console.error(error);
      } finally {
        setLoadingMore(false);
      }
    };

    fetchData();
  }, [categoryData, currentPage, startPrice, endPrice, sort, category, limit]);

  /* ---------------- Sidebar Visibility ---------------- */
  const showSidebar = false;

  if (brandNotFound) {
    return (
      <ProductNotFound
        type="brand"
        title="Brand Not Found"
        message="Sorry, the brand you are looking for does not exist or has been moved."
      />
    );
  }

  /* ---------------- JSX ---------------- */
  return (
    <div className="max-w-7xl mx-auto mt-5 px-4 sm:px-6 lg:px-8">
      <div className="flex flex-col md:flex-row gap-6">
        {/* Sidebar */}
        {showSidebar && (
          <div className="hidden md:block md:w-1/4">
            <Sidebar
              category={categoryData}
              minPrice={"0"}
              maxPrice={"0"}
            />
          </div>
        )}

        {/* Main Content */}
        <div className={`w-full ${showSidebar ? "md:w-3/4" : "md:w-full"}`}>
          {/* Brand Banner */}
          <BannerImage
            src={categoryData?.data?.bannerUrl || categoryData?.data?.imageUrl}
            alt={brandName || "Brand Banner"}
          />



          {/* Header Row */}
          <div className="flex items-center justify-between gap-4 mt-6 mb-2">
            <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight capitalize">
              {brandName || "Brand"}
            </h1>
            <div className="w-60">
              <SelectField
                name="sort"
                placeholder="Sort by"
                value={sort}
                onChange={(val) => {
                  setSort(val);
                  const params = new URLSearchParams(searchParams.toString());

                  if (val) params.set("sort", val);
                  else params.delete("sort");

                  params.set("page", "1");
                  fastPush(`/vendor/${slug}?${params.toString()}`);
                }}
                options={[
                  { value: "new", label: "New" },
                  { value: "a_to_z", label: "A to Z" },
                  { value: "z_to_a", label: "Z to A" },
                  { value: "price_low_to_high", label: "Price Low to High" },
                  { value: "price_high_to_low", label: "Price High to Low" },
                  { value: "top_selling", label: "Top Selling" },
                ]}
              />
            </div>
          </div>

          {/* Products */}
          {products?.length === 0 ? (
            <ProductNotFound
              type="brand"
              title={brandName ? `No Products Found for ${brandName}` : "Brand Not Found"}
              message={brandName ? "There are currently no items available under this brand." : "We couldn't find the brand or products you were looking for."}
            />
          ) : (
            <ProductGrid products={products} />
          )}

          {/* Load More Button */}
          {currentPage < totalPages && (
            <div className="mt-12 mb-10 flex justify-center">
              <Button
                onClick={() => setCurrentPage((prev) => prev + 1)}
                disabled={loadingMore}
                className="px-8 py-3 bg-black hover:bg-gray-800 text-white rounded-full font-medium transition shadow-sm text-sm flex items-center gap-2 cursor-pointer"
              >
                {loadingMore ? (
                  <>
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                    Loading...
                  </>
                ) : (
                  "Load More"
                )}
              </Button>
            </div>
          )}

          {/* Bottom Section: About Brand & FAQs */}
          {(categoryData?.data?.description || (categoryData?.data?.faqs && categoryData.data.faqs.length > 0)) && (
            <div className="mt-16 pt-10 border-t border-gray-100 space-y-10">
              {/* About Brand */}
              {categoryData?.data?.description && (() => {
                const decoded = decodeHtml(categoryData.data.description);
                const hasH2 = /<h2/i.test(decoded);
                return (
                  <section>
                    {!hasH2 && (
                      <h2 className="text-2xl font-extrabold text-gray-900 mb-4 tracking-tight">
                        About {brandName || "Brand"}
                      </h2>
                    )}
                    <div
                      className="prose prose-slate max-w-none break-words text-gray-700 text-sm md:text-base leading-relaxed [&_h2]:text-2xl [&_h2]:font-extrabold [&_h2]:text-gray-900 [&_h2]:mt-6 [&_h2]:mb-3 [&_p]:mb-4 [&_strong]:font-bold [&_strong]:text-gray-900 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5"
                      dangerouslySetInnerHTML={{ __html: decoded }}
                    />
                  </section>
                );
              })()}

              {/* FAQs Accordion */}
              <FaqAccordion items={categoryData?.data?.faqs} />
            </div>
          )}
        </div>
      </div>
    </div>



  );
}

export default function BrandPage({ params }: BrandPageProps) {
  return (
    <Suspense fallback={<div className="min-h-screen flex items-center justify-center">Loading brand...</div>}>
      <BrandPageContent params={params} />
    </Suspense>
  );
}
